import {
  Alert,
  AlertIcon,
  Box,
  Button,
  Flex,
  HStack,
  Input,
  Select,
  Stack,
  Table,
  Tbody,
  Text,
  Th,
  Thead,
  Tr,
  useColorModeValue,
} from '@chakra-ui/react';
import { useCallback, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import PendingPartnerItem from './PendingPartnerItem';
import { useTranslation } from 'i18n/TranslationProvider';
import {
  UnifiedEmptyState,
  UnifiedFilterBar,
  UnifiedLoadingState,
  UnifiedSurfaceCard,
} from 'components/layout/UnifiedPrimitives';
import { useUnifiedTableStyles } from 'components/layout/useUnifiedUiStyles';
import { useGetPendingPartners } from 'domains/admin/hooks/useGetPendingPartners';
import useApprovePartner from 'domains/admin/hooks/useApprovePartner';
import useRejectPartner from 'domains/admin/hooks/useRejectPartner';

const normalize = (value) =>
  String(value || '')
    .toLowerCase()
    .trim();

const PendingPartners = () => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { tableSx } = useUnifiedTableStyles();
  const mutedText = useColorModeValue('gray.600', 'gray.300');
  const panelBg = useColorModeValue(
    'rgba(255,255,255,0.92)',
    'rgba(13,23,53,0.72)',
  );

  const statusFilter = normalize(searchParams.get('status') || 'all');
  const query = searchParams.get('q') || '';
  const page = Math.max(
    Number.parseInt(searchParams.get('page') || '1', 10),
    1,
  );
  const limit = Math.max(
    Number.parseInt(searchParams.get('limit') || '25', 10),
    10,
  );

  const {
    pendingPartners,
    loading: fetchLoading,
    error: fetchError,
    refetch,
  } = useGetPendingPartners();

  const { approvePartner, loading: approveLoading } = useApprovePartner({
    onSettled: refetch,
  });
  const { rejectPartner, loading: rejectLoading } = useRejectPartner({
    onSettled: refetch,
  });

  const setParam = useCallback(
    (updates) => {
      const next = new URLSearchParams(searchParams);

      Object.entries(updates).forEach(([key, value]) => {
        if (value === undefined || value === null || value === '') {
          next.delete(key);
        } else {
          next.set(key, String(value));
        }
      });

      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  const handleSearchChange = (event) => {
    setParam({ q: event.target.value, page: 1 });
  };

  const handleStatusFilterChange = (event) => {
    setParam({ status: event.target.value, page: 1 });
  };

  // Local filtering logic
  const filteredPendingPartners = useMemo(() => {
    if (!pendingPartners) return [];

    return pendingPartners.filter((partner) => {
      // 1. Status Filter
      if (
        statusFilter === 'pending' &&
        partner.validationStatus !== 'pending'
      ) {
        return false;
      }
      if (statusFilter === 'new') {
        const fourteenDaysAgo = new Date();
        fourteenDaysAgo.setDate(fourteenDaysAgo.getDate() - 14);
        const createdAt = partner.createdAt
          ? new Date(partner.createdAt)
          : null;
        if (!createdAt || createdAt < fourteenDaysAgo) {
          return false;
        }
      }

      // 2. Search Query
      if (query) {
        const q = normalize(query);
        const searchString = [
          partner.displayName,
          partner.email,
          partner.companyName,
          partner.firebaseUid,
        ]
          .map((v) => normalize(v))
          .join(' ');

        if (!searchString.includes(q)) {
          return false;
        }
      }

      return true;
    });
  }, [pendingPartners, statusFilter, query]);

  // Local pagination
  const total = filteredPendingPartners.length;
  const totalPages = Math.ceil(total / limit);

  // Sync URL if current page exceeds total pages (e.g. after data changes)
  useEffect(() => {
    if (page > 1 && totalPages > 0 && page > totalPages) {
      setParam({ page: 1 });
    }
  }, [page, totalPages, setParam]);

  const currentPage = Math.min(page, totalPages || 1);

  const paginatedPartners = useMemo(() => {
    const start = (currentPage - 1) * limit;
    return filteredPendingPartners.slice(start, start + limit);
  }, [filteredPendingPartners, currentPage, limit]);

  const visibleFrom = total === 0 ? 0 : (currentPage - 1) * limit + 1;
  const visibleTo = total === 0 ? 0 : Math.min(currentPage * limit, total);

  if (fetchLoading) {
    return (
      <UnifiedSurfaceCard p={8}>
        <UnifiedLoadingState label={t('Loading partners...')} minH="120px" />
      </UnifiedSurfaceCard>
    );
  }

  if (fetchError) {
    return (
      <UnifiedSurfaceCard p={5}>
        <Alert status="error" borderRadius="12px">
          <AlertIcon />
          {t('Error')}: {fetchError?.message || fetchError}
        </Alert>
      </UnifiedSurfaceCard>
    );
  }

  return (
    <Stack spacing={4}>
      <UnifiedSurfaceCard p={5} bg={panelBg}>
        <Stack spacing={4}>
          <Flex
            justify="space-between"
            align={{ base: 'flex-start', md: 'center' }}
            gap={3}
            flexWrap="wrap"
          >
            <Text fontWeight="700">{t('View Partners')}</Text>
            <Button size="sm" variant="outline" onClick={() => refetch?.()}>
              {t('Refresh')}
            </Button>
          </Flex>

          <UnifiedFilterBar>
            <Box minW={{ base: '100%', md: '260px' }}>
              <Input
                value={query}
                placeholder={t('Search by name, email, company')}
                onChange={handleSearchChange}
              />
            </Box>
            <Box minW={{ base: '100%', md: '180px' }}>
              <Select value={statusFilter} onChange={handleStatusFilterChange}>
                <option value="all">{t('All partners')}</option>
                <option value="pending">{t('Pending')}</option>
                <option value="new">{t('New (14 days)')}</option>
              </Select>
            </Box>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setParam({ q: '', status: 'all', page: 1 })}
            >
              {t('Reset')}
            </Button>
          </UnifiedFilterBar>
        </Stack>
      </UnifiedSurfaceCard>

      {total === 0 ? (
        <UnifiedSurfaceCard p={4}>
          <UnifiedEmptyState
            title={t('No partners found.')}
            description={t(
              'Try broadening the current search or status filter.',
            )}
            minH="140px"
          />
        </UnifiedSurfaceCard>
      ) : (
        <UnifiedSurfaceCard p={4} overflow="hidden">
          <Flex
            justify="space-between"
            align={{ base: 'flex-start', md: 'center' }}
            mb={3}
            gap={2}
            flexWrap="wrap"
          >
            <Text fontSize="sm" color={mutedText}>
              {t('Showing')} {visibleFrom}-{visibleTo} {t('of')} {total}
            </Text>
            <HStack spacing={2}>
              <Text fontSize="sm" color={mutedText}>
                {t('Rows')}
              </Text>
              <Select
                size="sm"
                value={String(limit)}
                onChange={(event) =>
                  setParam({ limit: event.target.value, page: 1 })
                }
                w="92px"
              >
                <option value="25">25</option>
                <option value="50">50</option>
                <option value="100">100</option>
              </Select>
            </HStack>
          </Flex>

          <Box overflowX="auto">
            <Table size="sm" sx={{ minWidth: '1320px', ...tableSx }}>
              <Thead>
                <Tr>
                  <Th>{t('User')}</Th>
                  <Th>{t('Email')}</Th>
                  <Th>{t('Company')}</Th>
                  <Th>{t('Status')}</Th>
                  <Th>{t('Quarter Score')}</Th>
                  <Th>{t('Delay')}</Th>
                  <Th>{t('Documents')}</Th>
                  <Th>{t('Actions')}</Th>
                </Tr>
              </Thead>
              <Tbody>
                {paginatedPartners.map((partner) => (
                  <PendingPartnerItem
                    key={partner.firebaseUid || partner.id}
                    partner={partner}
                    tableMode
                    onApprove={approvePartner}
                    onReject={rejectPartner}
                    approveLoading={approveLoading}
                    rejectLoading={rejectLoading}
                  />
                ))}
              </Tbody>
            </Table>
          </Box>

          <Flex
            mt={4}
            justify="space-between"
            align={{ base: 'flex-start', md: 'center' }}
            gap={3}
            flexWrap="wrap"
          >
            <Text fontSize="sm" color={mutedText}>
              {t('Total')}: {total}
            </Text>
            <HStack spacing={2}>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setParam({ page: currentPage - 1 })}
                isDisabled={currentPage <= 1}
              >
                {t('Previous')}
              </Button>
              <Text fontSize="sm" minW="100px" textAlign="center">
                {t('Page')} {currentPage}
                {totalPages ? ` / ${totalPages}` : ''}
              </Text>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setParam({ page: currentPage + 1 })}
                isDisabled={!totalPages || currentPage >= totalPages}
              >
                {t('Next')}
              </Button>
            </HStack>
          </Flex>
        </UnifiedSurfaceCard>
      )}
    </Stack>
  );
};

export default PendingPartners;
